import { useEffect, useId, useRef, useState } from 'react';
import type { FC, FormEvent } from 'react';

import { ChevronRightIcon } from '@interstate/components/Icons/ChevronRightIcon';
import { AiLogo } from '@makemydeal/dr-shared-components';

import {
    DEAL_ASSISTANT_ENTRY_LABEL,
    DEAL_ASSISTANT_ENTRY_PROMPT_INTERVAL_MS,
    DEAL_ASSISTANT_ENTRY_PROMPTS,
    DEAL_ASSISTANT_UNAVAILABLE_LABELS
} from '../constants';
import { useDealAssistant, useDealAssistantAvailability } from '../state/DealAssistantContext';
import { DealAssistantTheme } from './DealAssistantTheme';
import { EntryForm, EntryInput, EntryReason, EntrySendButton } from './DealAssistantEntry.style';

export const DealAssistantEntry: FC = () => {
    const { isOpen, status, open, sendMessage, trackInputInteraction } = useDealAssistant();
    const { isAvailable, reason } = useDealAssistantAvailability();
    const reasonId = useId();
    const [text, setText] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [promptIndex, setPromptIndex] = useState(0);
    const hasTrackedInteractionRef = useRef(false);

    useEffect(() => {
        if (isFocused || text) {
            return undefined;
        }
        const interval = setInterval(
            () => setPromptIndex((current) => (current + 1) % DEAL_ASSISTANT_ENTRY_PROMPTS.length),
            DEAL_ASSISTANT_ENTRY_PROMPT_INTERVAL_MS
        );
        return () => clearInterval(interval);
    }, [isFocused, text]);

    if (isOpen) {
        return null;
    }

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (!isAvailable) {
            return;
        }
        if (status === 'loading') {
            open();
            return;
        }
        const question = text.trim();
        setText('');
        open();
        if (question) {
            void sendMessage(question);
        }
    };

    return (
        <DealAssistantTheme>
            <EntryForm onSubmit={handleSubmit} data-testid="deal-assistant-entry">
                <AiLogo className="deal-assistant-entry-logo" aria-hidden="true" />
                <EntryInput
                    type="text"
                    $promptIndex={promptIndex}
                    value={text}
                    placeholder={DEAL_ASSISTANT_ENTRY_PROMPTS[promptIndex]}
                    aria-label={DEAL_ASSISTANT_ENTRY_LABEL}
                    data-testid="deal-assistant-entry-input"
                    aria-describedby={reason ? reasonId : undefined}
                    onFocus={() => {
                        setIsFocused(true);
                        if (!hasTrackedInteractionRef.current) {
                            hasTrackedInteractionRef.current = true;
                            trackInputInteraction();
                        }
                    }}
                    onBlur={() => setIsFocused(false)}
                    onChange={(event) => setText(event.target.value)}
                />
                <EntrySendButton
                    type="submit"
                    aria-label="Open Deal AI"
                    data-testid="deal-assistant-entry-send"
                    disabled={!isAvailable}
                >
                    <ChevronRightIcon />
                </EntrySendButton>
            </EntryForm>
            {reason && (
                <EntryReason id={reasonId} role="status" data-testid="deal-assistant-entry-reason">
                    {DEAL_ASSISTANT_UNAVAILABLE_LABELS[reason]}
                </EntryReason>
            )}
        </DealAssistantTheme>
    );
};
